import { motion } from "framer-motion";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  FaArrowRight,
  FaBuilding,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaUser,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/SelfSync Logo.png";
import { useNotification } from "../../contextApi/NotificationProvider";
import signupIllustration from "../../Images/signuppage (2).webp";
import { signUpUser, verifyUserOtp } from "../../redux/ReduxSlices/authSlice";
import OtpVerify from "../Otp/OtpVerify";

const SignupPage = () => {
  const [passwordStrength, setPasswordStrength] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedTab, setSelectedTab] = useState("organization");
  const { showNotification } = useNotification();
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const { loading, error, user } = useSelector((state) => state.auth);
  const { userId } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    company: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onSubmit = async (data) => {
    if (selectedTab === "organization" && !data.company) {
      alert("Company name is required for organization signup");
      return;
    }

    const payload = {
      user: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        user_type: selectedTab == "organization" ? "owner" : "individual",
        // gender: "m",
      },
      company: {
        company_name:
          selectedTab == "organization" ? data.company : "nocompany",
        company_email: data.email,
      },
    };

    try {
      const result = await dispatch(signUpUser(payload)).unwrap();

      if (result.success === false) {
        showNotification(result.message || "Signup failed", "error", 8000);
      } else {
        showNotification(
          "Account created successfully! Please verify with OTP send to your email",
          "success",
          8000
        );
        setShowOtpPopup(true);
      }
    } catch (err) {
      const errorMsg =
        err?.message || err?.error || "Something went wrong. Please try again";
      showNotification(errorMsg, "error", 5000);
      console.error("Signup error:", err);
    }
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return "Weak";
    if (strength === 3 || strength === 4) return "Medium";
    if (strength === 5) return "Strong";
  };

  // Grab RHF's internal onChange from register
  const { onChange: passwordOnChange, ...restPasswordRegister } = register(
    "password",
    {
      required: "Password is required",
      minLength: {
        value: 6,
        message: "Password must be at least 6 characters",
      },
      validate: (value) => {
        const rules = [
          { regex: /[A-Z]/, message: "Must include an uppercase letter" },
          { regex: /[a-z]/, message: "Must include a lowercase letter" },
          { regex: /[0-9]/, message: "Must include a number" },
          {
            regex: /[^A-Za-z0-9]/,
            message: "Must include a special character",
          },
        ];
        for (let rule of rules) {
          if (!rule.regex.test(value)) return rule.message;
        }
        return true;
      },
    }
  );

  // Custom handler that updates strength and revalidates confirmPassword
  const onPasswordChange = (e) => {
    const value = e.target.value;
    setPasswordStrength(getPasswordStrength(value));
    passwordOnChange(e); // tell React Hook Form the field changed
    if (watch("confirmPassword")) {
      trigger("confirmPassword"); // revalidate confirmPassword
    }
  };

  const handleLoginSuccess = (credentialResponse) => {
    const token = credentialResponse.credential;
  };

  const handleLoginFailure = () => {
    console.error("Google Login Failed");
    alert("Google login failed. Please try again.");
  };

  const handleOtpSubmit = async (otp) => {
    try {
      const result = await dispatch(
        verifyUserOtp({ otp, user_id: userId })
      ).unwrap();

      if (result.success) {
        showNotification(
          "OTP Verified Successfully! Please Login",
          "success",
          5000
        );
        navigate("/dashboard");
      } else {
        showNotification(result.message || "Invalid OTP", "error", 5000);
      }
    } catch (error) {
      showNotification(
        error || "OTP verification failed. Try again",
        "error",
        5000
      );
    } finally {
      setShowOtpPopup(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <div className="md:w-1/2 bg-gradient-to-br from-purple-600 to-blue-500 p-8 flex flex-col">
        <div className="max-md:flex max-md:justify-center max-md:mb-2">
          <a
            className="bg-white w-28 flex justify-center text-center rounded-full"
            href="https://selfsync.ai/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={logo} alt="Logo" className="h-9 max-md:h-7" />
          </a>
        </div>
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex-grow flex flex-col justify-center items-center text-white"
        >
          <h1 className="text-4xl font-bold mb-4 text-center">
            Create Your Account
          </h1>
          <p className="text-xl mb-8 text-center opacity-90">
            Join the world's first AI-powered talent assessment platform
          </p>

          <div className="w-full max-w-md mb-8">
            <img
              src={signupIllustration}
              alt="Signup Illustration"
              className="rounded-lg shadow-xl"
            />
          </div>

          <div className="w-full max-w-md space-y-3">
            <div className="flex items-center">
              <FaArrowRight className="text-green-300 mr-2" />
              <span>AI-driven personality insights</span>
            </div>
            <div className="flex items-center">
              <FaArrowRight className="text-green-300 mr-2" />
              <span>Advanced response analysis</span>
            </div>
            <div className="flex items-center">
              <FaArrowRight className="text-green-300 mr-2" />
              <span>Empower your hiring process</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Signup Form */}
      <motion.div
        initial={{ x: 200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="md:w-1/2 flex items-center justify-center p-8"
      >
        <div className="w-full max-w-xl bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6">
            <div className="w-full flex justify-center items-center mb-4">
              <img src={logo} alt="Logo" className="h-11" />
            </div>
            <div className="flex justify-center mb-4">
              <div className="relative bg-gray-100 dark:bg-gray-800 rounded-full p-1 flex shadow-inner w-fit">
                <div
                  className={`absolute top-1 h-[calc(100%-0.5rem)] rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 transition-all duration-300 ease-in-out shadow-md ${
                    selectedTab === "organization"
                      ? "left-[calc(50%+0.125rem)] w-[calc(50%-0.25rem)]"
                      : "left-1 w-[calc(50%-0.25rem)]"
                  }`}
                />

                <div
                  className={`absolute top-0 w-1/2 h-full rounded-full btn-gradient-help opacity-20 blur-md transition-all duration-500 ${
                    selectedTab === "organization"
                      ? "left-1/2 btn-gradient-help"
                      : "left-0"
                  }`}
                />

                <button
                  type="button"
                  className={`relative z-10 py-2 rounded-full transition-all duration-300 font-medium text-sm flex-1 min-w-[140px] px-4 ${
                    selectedTab === "student"
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  }`}
                  onClick={() => setSelectedTab("student")}
                >
                  Individual
                </button>

                <button
                  type="button"
                  className={`relative z-10 py-2 rounded-full transition-all duration-300 font-medium text-sm flex-1 min-w-[140px] px-4 ${
                    selectedTab === "organization"
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  }`}
                  onClick={() => setSelectedTab("organization")}
                >
                  Business
                </button>
              </div>
            </div>

            {selectedTab === "student" ? (
              <div className="mb-4 text-xs text-gray-500 cursor-pointer text-start max-md:text-center">
                To sign up as an individual, please select the{" "}
                <strong>Individual</strong> tab and continue with the
                credentials below. You’ll be able to assess yourself only.
              </div>
            ) : (
              <div className="mb-4 text-xs text-gray-500 cursor-pointer text-start max-md:text-center">
                To sign up as an organization or business, please select the{" "}
                <strong>Business</strong> tab and continue with the credentials
                below. You’ll be able to assess your employees and new hires.
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400" />
                    </div>
                    <input
                      {...register("firstName", {
                        required: "First name is required",
                        minLength: {
                          value: 2,
                          message: "First name must be at least 2 characters",
                        },
                      })}
                      onChange={handleChange}
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="John"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400" />
                    </div>
                    <input
                      {...register("lastName", {
                        required: "Last name is required",
                        minLength: {
                          value: 2,
                          message: "Last name must be at least 2 characters",
                        },
                      })}
                      onChange={handleChange}
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Doe"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address{" "}
                  <span className="text-xs text-gray-500 ml-1"></span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Enter a valid email address",
                      },
                    })}
                    onChange={handleChange}
                    className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {selectedTab === "organization" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaBuilding className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register("company", {
                        required: "Company name is required",
                        minLength: {
                          value: 2,
                          message: "Company name must be at least 2 characters",
                        },
                      })}
                      onChange={handleChange}
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Your Company"
                    />
                  </div>
                  {errors.company && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.company.message}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="flex justify-between text-sm font-medium text-gray-700 mb-1">
                  Password
                  {passwordStrength && (
                    <div>
                      <span className="ml-4 text-xs mr-2">Strength:</span>
                      <span
                        className={`text-xs font-medium ${
                          passwordStrength === "Weak"
                            ? "text-red-500"
                            : passwordStrength === "Medium"
                            ? "text-yellow-500"
                            : "text-green-600"
                        }`}
                      >
                        {passwordStrength}
                      </span>
                    </div>
                  )}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...restPasswordRegister}
                    onChange={onPasswordChange}
                    autoComplete="off"
                    className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="••••••••"
                  />
                  <div
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="text-gray-500" />
                    ) : (
                      <FaEye className="text-gray-500" />
                    )}
                  </div>
                </div>

                {errors.password && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirmPassword", {
                      required: "Confirm your password",
                      // validate: (value) =>
                      //   value === watch("password") || "Passwords do not match",
                      validate: (value) => {
                        const pw = watch("password");
                        const isValid = value === pw;
                        console.log(
                          "Validating confirmPassword:",
                          value,
                          pw,
                          isValid
                        );
                        return isValid || "Passwords do not match";
                      },
                    })}
                    className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="••••••••"
                  />
                  <div
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash className="text-gray-500" />
                    ) : (
                      <FaEye className="text-gray-500" />
                    )}
                  </div>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 text-white font-medium rounded-lg shadow-md transition duration-300 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "btn-gradient"
                }`}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Already have an account?
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => navigate("/login")}
                  className="w-full py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  Sign in to your account
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {showOtpPopup && (
        <OtpVerify
          onSubmit={handleOtpSubmit}
          onClose={() => setShowOtpPopup(false)}
        />
      )}
    </div>
  );
};

export default SignupPage;
